import { useEffect, useState } from "react";
import { Link, useRevalidator, useRouteLoaderData, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";
import UserInfo from "../common/userInfo";
import type { Assignment } from "./types";
import { getFileNameFromUrl, myAxios } from "../common/functions";
import { useAuthStore, User } from "../main/authStore";
import AssignmentDetailView, { getRequiredFormStatus } from "./AssignmentDetailView";
import FileSystem from "../common/fileSystem";

export default function AssignmentDetail() {
    const user = useAuthStore(state => state.user) as User;
    const assignment = useRouteLoaderData("assignment") as Assignment;
    const [assignmentItem, setAssignmentItem] = useState<Assignment>(assignment);
    const submit = useSubmit();
    const { revalidate } = useRevalidator();
    const [showJsaModal, setShowJsaModal] = useState(false);
    const [showJobTagModal, setShowJobTagModal] = useState(false);

    useEffect(() => {
        setAssignmentItem(assignment);
    }, [assignment]);

    const setAssignmentItems = (updater: Function) => {
        setAssignmentItem(currentAssignment => {
            const nextItems = typeof updater === "function" ? updater([currentAssignment]) : updater;
            return Array.isArray(nextItems) ? (nextItems[0] ?? currentAssignment) : currentAssignment;
        });
    };

    const jsaRequired = assignmentItem.waiveJSA !== "yes";
    const laborCategory = assignmentItem.laborCategory || "";
    const canShowForms = ["Created", "Rejected"].includes(assignmentItem.status);
    const jsaStatus = assignmentItem.jsaStatus || "Not started";
    const jobTagStatus = assignmentItem.jobTagStatus || "Not started";
    const hasJsaData = Boolean(
        assignmentItem.jsaSubmitTime ||
        assignmentItem.jsaSaveTime ||
        assignmentItem.jobSafetyAnalysisTemplate ||
        assignmentItem.jobSafetyAnalysisTemplateDoc ||
        assignmentItem.nearestMedicalFacility ||
        assignmentItem.jobSafetyAnalysisContent ||
        assignmentItem.jobSafetyAnalysisSignId ||
        assignmentItem.jsaFileId
    );
    const hasJobTagData = Boolean(
        assignmentItem.travelStartTime ||
        assignmentItem.workStartTime ||
        assignmentItem.hadLunch ||
        assignmentItem.workEndTime ||
        assignmentItem.travelEndTime ||
        assignmentItem.workFinished ||
        assignmentItem.workRequired ||
        assignmentItem.workPerformed ||
        assignmentItem.additionalInfo ||
        assignmentItem.sign
    );
    const checklistForms = laborCategory === "Locating" ? [
        { key: "locatingPreChecklist", label: "Pre Checklist", path: "PreChecklist", phase: "pre" as const },
        { key: "locatingPostChecklist", label: "Post Checklist", path: "PostChecklist", phase: "post" as const },
    ] : laborCategory === "Potholing" ? [
        { key: "potholingPreChecklist", label: "Pre Checklist", path: "PreChecklist", phase: "pre" as const },
        { key: "potholingPostChecklist", label: "Post Checklist", path: "PostChecklist", phase: "post" as const },
    ] : [];
    const requiredFormCards = [
        ...checklistForms.map(form => ({
            label: form.label,
            description: `${laborCategory} checklist`,
            path: form.path,
            status: getRequiredFormStatus(assignmentItem.forms, form.key),
            phase: form.phase,
        })),
        {
            label: "Pre Vehicle",
            description: "Vehicle inspection before work",
            path: "PreVehicle",
            status: assignmentItem.isPreDriver !== null ? "Submitted" : "Not Started",
            phase: "pre" as const,
        },
        {
            label: "Pre Trailer",
            description: "Trailer inspection before work",
            path: "PreTrailer",
            status: assignmentItem.hasPreTrailer !== null ? "Submitted" : "Not Started",
            phase: "pre" as const,
        },
        {
            label: "Post Vehicle",
            description: "Vehicle inspection after work",
            path: "PostVehicle",
            status: assignmentItem.isPostDriver !== null ? "Submitted" : "Not Started",
            phase: "post" as const,
        },
        {
            label: "Post Trailer",
            description: "Trailer inspection after work",
            path: "PostTrailer",
            status: assignmentItem.hasPostTrailer !== null ? "Submitted" : "Not Started",
            phase: "post" as const,
        },
    ];
    const preFormCards = requiredFormCards.filter(form => form.phase === "pre");
    const postFormCards = requiredFormCards.filter(form => form.phase === "post");
    const jsaDone = !jsaRequired || assignmentItem.jsaStatus === "Submitted";
    const jobTagDone = assignmentItem.jobTagStatus === "Submitted";
    const preFormsDone = preFormCards.every(form => form.status === "Submitted");
    const fields = [
        {
            label: "Project",
            value: assignmentItem.projectId ? <Link to={`/Projects/${assignmentItem.projectId}`}>{assignmentItem.projectName}</Link> : assignmentItem.projectName,
        },
        {
            label: "Work ID",
            value: <Link to={`/Works/${assignmentItem.workId}`}>{assignmentItem.workName || assignmentItem.workId}</Link>,
        },
        { label: "Category", value: assignmentItem.workCategory },
        { label: "Sub Category", value: assignmentItem.workSubCategory },
        { label: "Labor Category", value: assignmentItem.laborCategory },
        {
            label: "Location",
            value: assignmentItem.location ? (
                <a
                    href={assignmentItem.coords ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignmentItem.coords)}` : undefined}
                    target="_blank"
                    rel="noreferrer"
                >
                    {assignmentItem.location}
                </a>
            ) : null,
        },
        {
            label: "Schedule Time",
            value: assignmentItem.allDay === "yes"
                ? assignmentItem.startTime.slice(0, 10) + " ~ " + assignmentItem.endTime.slice(0, 10)
                : assignmentItem.startTime + " ~ " + assignmentItem.endTime,
        },
        {
            label: "Site Contact",
            value: assignmentItem.siteContactId ? (
                <Link to={`/Contacts/${assignmentItem.siteContactId}`}>
                    {[assignmentItem.siteContactName, assignmentItem.siteContactPhone].filter(Boolean).join(" - ")}
                </Link>
            ) : null,
        },
        { label: "Supervisor", value: <UserInfo id={assignmentItem.supervisorId} name={assignmentItem.supervisorName} /> },
        { label: "CAD Required", value: assignmentItem.cadRequired },
        { label: "Report Required", value: assignmentItem.reportRequired },
        { label: "Waive JSA", value: assignmentItem.waiveJSA },
        { label: "Job Tag Location", value: assignmentItem.jobTagLocation },
        { label: "JSA Initiator", value: <UserInfo id={assignmentItem.leadId} name={assignmentItem.leadName} /> },
        { label: "Creator", value: <div><UserInfo id={assignmentItem.creatorId} name={assignmentItem.creatorName} />{assignmentItem.createdAt}</div> },
        { label: "Updater", value: <div><UserInfo id={assignmentItem.updaterId} name={assignmentItem.updaterName} />{assignmentItem.updatedAt}</div> },
        { label: "Void Reason", value: assignmentItem.voidReason, show: !!assignmentItem.voidReason },
        { label: "Status", value: assignmentItem.status },
    ];

    const handleDownloadJobTag = async () => {
        if (!assignmentItem.id) return;
        const formData = new FormData();
        formData.append("id", String(assignmentItem.id));
        const res = await myAxios({ fileName: "assignments/getJobTagPDF.php", formData });
        if (!res) return;
        const url = res.data;
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(url) || "download");
        } catch (error) {
            window.alert("Error downloading file:" + error);
        }
    };

    const handleDownloadJsa = async () => {
        if (!assignmentItem.jsaFileId) {
            window.alert("JSA PDF is not available.");
            return;
        }
        const formData = new FormData();
        formData.append("action", "downloadFile");
        formData.append("fileId", assignmentItem.jsaFileId);
        formData.append("fileName", `JSA ${assignmentItem.projectName || assignmentItem.id || ""}`.trim());
        const res = await myAxios({ fileName: "server/fileStorage.php", formData });
        if (!res) return;
        const url = res.data;
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(url) || "download");
        } catch (error) {
            window.alert("Error downloading file:" + error);
        }
    };

    const handleSubmitAssignment = () => {
        const formData = new FormData();
        formData.append("id", String(assignmentItem.id));
        submit(formData, { method: "post", action: "Submit" });
    };
    const handleWithdrawAssignment = () => {
        if (!window.confirm("Are you sure to withdraw this assignment?")) return;
        const formData = new FormData();
        formData.append("id", String(assignmentItem.id));
        submit(formData, { method: "post", action: "Withdraw" });
    };

    return (
        <>
            <AssignmentDetailView
                assignment={assignmentItem}
                fields={fields}
                filesSection={
                    <FileSystem
                        item={assignmentItem}
                        fileSystem={[{
                            title: "Files",
                            folderIdLabel: "folderId",
                            folderKey: "assignmentFiles",
                        }]}
                        setItems={setAssignmentItems}
                        sqlTable="assignments"
                        itemLabel="assignment"
                        idLabel="id"
                        filesKey="assignmentFiles"
                        singleFolderId="folderId"
                        readOnly={user.assignments === "no"}
                        onFilesChanged={revalidate}
                    />
                }
                canShowForms={canShowForms}
                showJsaCard={jsaRequired && assignmentItem.leadId === user.id}
                hasJsaData={hasJsaData}
                jsaRequired={jsaRequired}
                jsaStatus={jsaStatus}
                preFormCards={preFormCards}
                jsaDone={jsaDone}
                preFormsDone={preFormsDone}
                hasJobTagData={hasJobTagData}
                jobTagStatus={jobTagStatus}
                jobTagDone={jobTagDone}
                postFormCards={postFormCards}
                showJsaModal={showJsaModal}
                showJobTagModal={showJobTagModal}
                onOpenJsaModal={() => setShowJsaModal(true)}
                onCloseJsaModal={() => setShowJsaModal(false)}
                onOpenJobTagModal={() => setShowJobTagModal(true)}
                onCloseJobTagModal={() => setShowJobTagModal(false)}
                onDownloadJsa={handleDownloadJsa}
                onDownloadJobTag={handleDownloadJobTag}
                onSubmitAssignment={handleSubmitAssignment}
                onWithdrawAssignment={handleWithdrawAssignment}
            />
        </>
    );
}
